import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Card, Center, Loader, Stack, Text, Title } from '@mantine/core';
import { useAuth } from './use-auth';
import { authService } from './auth-service';
import { sanitizeRedirectPath } from '@/lib/auth/redirect-sanitizer';
import { OAUTH_STORAGE_KEYS } from './oauth-storage';

export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError(`Authentication failed: ${errorParam}`);
        return;
      }

      if (!code) {
        setError('No authorization code received');
        return;
      }

      const storedState = sessionStorage.getItem(OAUTH_STORAGE_KEYS.STATE);
      if (!state || !storedState || state !== storedState) {
        sessionStorage.removeItem(OAUTH_STORAGE_KEYS.STATE);
        sessionStorage.removeItem(OAUTH_STORAGE_KEYS.REDIRECT_PATH);
        setError('Invalid authentication state. Possible CSRF attack detected.');
        return;
      }

      try {
        const response = await authService.handleSsoCallback('google', code);
        await login(response.token);

        const storedPath = sessionStorage.getItem(OAUTH_STORAGE_KEYS.REDIRECT_PATH);
        const redirectPath = sanitizeRedirectPath(storedPath);
        sessionStorage.removeItem(OAUTH_STORAGE_KEYS.REDIRECT_PATH);
        sessionStorage.removeItem(OAUTH_STORAGE_KEYS.STATE);

        void navigate(redirectPath, { replace: true });
      } catch (err) {
        sessionStorage.removeItem(OAUTH_STORAGE_KEYS.STATE);
        setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    };

    void handleCallback();
  }, [searchParams, login, navigate]);

  if (error) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--v2-color-neutral-5)',
        }}
      >
        <Card shadow="md" padding="xl" radius="lg" style={{ width: 400, maxWidth: '90%' }}>
          <Stack gap="lg">
            <Center>
              <Title order={2}>Authentication Failed</Title>
            </Center>

            <Alert color="red" variant="light" className="login-error">
              {error}
            </Alert>

            <Button
              fullWidth
              size="md"
              variant="default"
              onClick={() => {
                void navigate('/login', { replace: true });
              }}
            >
              Return to Login
            </Button>
          </Stack>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--v2-color-neutral-5)',
      }}
    >
      <Card shadow="md" padding="xl" radius="lg" style={{ width: 400, maxWidth: '90%' }}>
        <Stack gap="lg" align="center">
          <Title order={2}>Signing in...</Title>
          <Text c="dimmed" size="sm">
            Please wait while we complete your authentication
          </Text>
          <Loader size="lg" />
        </Stack>
      </Card>
    </Box>
  );
}
